/**
 * Pure builder + validator for the "create a materialized projection" wizard.
 *
 * No pict / DOM / network dependencies — just deterministic transforms of plain state, so the create
 * flow's body construction + validation can be unit-tested in isolation. Mirrors the server contract in
 * services/platform_data_operations/source/endpoints/Projection-Endpoints.js (Name regex, Target customer
 * silo prefix, OperationType allow-list, required OperationConfiguration) so the user never round-trips to
 * a 400. The wizard supports the Extraction operation (pick a source entity → project columns); the other
 * operation types are listed for completeness / later tiers.
 */

const NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/;

const OPERATION_TYPES =
[
	'Extraction', 'Unnest', 'Aggregation', 'Histogram', 'Intersection', 'PassthroughClone', 'SQLAggregate', 'SQLJoin',
];

/**
 * Map a JSON-Schema scalar type (from a meadow /Schema response) to a meadow DDL column Type for the lake
 * TargetSchema. Unsized strings become Text (a sized String silently drops longer values on every run and
 * can't be widened without dropping the table — the projection-agent-guide "learned the hard way" trap).
 * @param {string} pJsonType
 * @param {string} [pSize]
 * @returns {string}
 */
function jsonTypeToMeadow(pJsonType, pSize)
{
	switch (pJsonType)
	{
		case 'integer': return 'Integer';
		case 'number': return 'Numeric';
		case 'boolean': return 'Boolean';
		case 'string':
		default:
			return (!pSize || pSize === 'Default') ? 'Text' : 'String';
	}
}

/**
 * Normalize a raw database column type (from /mapper/introspect of an imported lake clone — Postgres/MSSQL
 * types like bigint/varchar/numeric) to a JSON-Schema scalar type, so imported-clone columns flow through
 * the same field-builder + jsonTypeToMeadow as projection (/PrivateDataLake/Schema) and entity (/Schema)
 * columns. Unknown / exotic types default to string (safe — never silently mis-sizes a numeric column).
 * @param {string} pDbType
 * @returns {string}
 */
function dbTypeToJsonType(pDbType)
{
	const tmpType = String(pDbType || '').toLowerCase().trim();
	if (tmpType.startsWith('interval')) { return 'string'; }
	if (/serial/.test(tmpType) || /^(big|small|tiny)?int/.test(tmpType)) { return 'integer'; }
	if (/^(numeric|decimal|dec|real|double|float|money)/.test(tmpType)) { return 'number'; }
	if (/^bool/.test(tmpType)) { return 'boolean'; }
	if (/json/.test(tmpType)) { return 'object'; }
	return 'string';
}

/**
 * Derive the source address for an Extraction from a Headlight entity.
 * @param {{ SourceConnectionPrefix?: string, Entity: string, Filter?: string }} pState
 * @returns {string}
 */
function buildSourceAddress(pState)
{
	const tmpFilter = (pState.Filter || '').trim();
	if (pState.SourceType === 'lake' && pState.LakeSource)
	{
		// Lake source: an existing computed projection or imported clone, already addressed as lake/<physical>.
		return tmpFilter ? `${pState.LakeSource}/FilteredTo/${tmpFilter}` : pState.LakeSource;
	}
	const tmpPrefix = pState.SourceConnectionPrefix || 'headlight_api';
	const tmpEntity = pState.Entity;
	return tmpFilter ? `${tmpPrefix}/${tmpEntity}/FilteredTo/${tmpFilter}` : `${tmpPrefix}/${tmpEntity}`;
}

/**
 * Build the POST /1.0/Projection body for an Extraction from the wizard state.
 * @param {object} pState - { IDCustomer, Name, Entity, SourceConnectionPrefix?, Filter?, GUIDTemplate, Description?, fields:[{ SourceField, OutCol?, JsonType, Size?, Include? }] }
 * @returns {object} the POST body
 */
function buildExtractionBody(pState)
{
	const tmpDataset = `C${pState.IDCustomer}_${pState.Name}`;
	const tmpProjection = {};
	const tmpColumns = [];
	const tmpFields = (pState.fields || []).filter((pField) => pField && pField.Include !== false);
	for (const tmpField of tmpFields)
	{
		const tmpOutCol = tmpField.OutCol || tmpField.SourceField;
		tmpProjection[tmpOutCol] = `{~D:Record.${tmpField.SourceField}~}`;
		const tmpColumn = { Column: tmpOutCol, Type: jsonTypeToMeadow(tmpField.JsonType, tmpField.Size) };
		if (tmpField.Size && tmpField.Size !== 'Default' && tmpColumn.Type === 'String')
		{
			tmpColumn.Size = tmpField.Size;
		}
		tmpColumns.push(tmpColumn);
	}

	const tmpBody =
	{
		IDCustomer: parseInt(pState.IDCustomer, 10),
		Name: pState.Name,
		OperationType: 'Extraction',
		Source: buildSourceAddress(pState),
		Target: `lake/${tmpDataset}`,
		OperationConfiguration:
		{
			Entity: tmpDataset,
			GUIDTemplate: pState.GUIDTemplate,
			Projection: tmpProjection,
		},
	};
	if (tmpColumns.length > 0)
	{
		tmpBody.TargetSchema = { Columns: tmpColumns };
	}
	if (pState.Description)
	{
		tmpBody.Description = pState.Description;
	}
	return tmpBody;
}

/**
 * Validate the wizard state for an Extraction, mirroring the server's checks.
 * @param {object} pState
 * @returns {{ Valid: boolean, Errors: Array<string> }}
 */
function validateExtractionState(pState)
{
	const tmpErrors = [];
	const tmpID = parseInt(pState && pState.IDCustomer, 10);
	if (!(tmpID > 0))
	{
		tmpErrors.push('A customer is required.');
	}
	if (!pState || !pState.Name || !NAME_REGEX.test(pState.Name))
	{
		tmpErrors.push('Name must start with a letter and contain only letters, numbers, and underscores.');
	}
	const tmpIsLake = !!(pState && pState.SourceType === 'lake');
	if (!pState || (tmpIsLake ? !pState.LakeSource : !pState.Entity))
	{
		tmpErrors.push(tmpIsLake ? 'A source data-lake table is required.' : 'A source entity is required.');
	}
	else if (tmpIsLake && tmpID > 0 && pState.Name && pState.LakeSource === `lake/C${tmpID}_${pState.Name}`)
	{
		tmpErrors.push('The new projection name must differ from the source lake table.');
	}
	const tmpFields = (pState && Array.isArray(pState.fields) ? pState.fields : []).filter((pField) => pField && pField.Include !== false);
	if (tmpFields.length < 1)
	{
		tmpErrors.push('Select at least one column to project.');
	}
	if (!pState || !pState.GUIDTemplate || pState.GUIDTemplate.indexOf('{~D:Record.') < 0)
	{
		tmpErrors.push('The GUID template must reference a record field, e.g. {~D:Record.IDProject~}.');
	}
	// Target customer-silo check (mirrors Projection-Endpoints: the 2nd path segment must start with C<id>_).
	if (tmpID > 0 && pState && pState.Name)
	{
		const tmpTarget = `lake/C${tmpID}_${pState.Name}`;
		const tmpSegment = tmpTarget.replace(/^\/+/, '').split('/')[1] || '';
		if (!tmpSegment.startsWith(`C${tmpID}_`))
		{
			tmpErrors.push('The target must live in this customer\'s lake silo.');
		}
	}
	return { Valid: tmpErrors.length === 0, Errors: tmpErrors };
}

/**
 * Build the POST /1.0/Projection body for a PassthroughClone — a 1:1 copy of a lake table into a new,
 * registered (queryable) lake table. Used to "projectionize" a raw imported clone table so its rows become
 * browsable over the API. No column picking (the operation copies all columns + infers the schema).
 * @param {{ IDCustomer, Name, LakeSource:string, Description? }} pState
 * @returns {object}
 */
function buildPassthroughCloneBody(pState)
{
	const tmpDataset = `C${pState.IDCustomer}_${pState.Name}`;
	const tmpBody =
	{
		IDCustomer: parseInt(pState.IDCustomer, 10),
		Name: pState.Name,
		OperationType: 'PassthroughClone',
		Source: pState.LakeSource,
		Target: `lake/${tmpDataset}`,
		OperationConfiguration: { Entity: tmpDataset, GUIDTemplate: pState.GUIDTemplate, Source: pState.LakeSource },
	};
	if (pState.Description)
	{
		tmpBody.Description = pState.Description;
	}
	return tmpBody;
}

/**
 * Validate a PassthroughClone (projectionize) state.
 * @param {object} pState
 * @returns {{ Valid: boolean, Errors: Array<string> }}
 */
function validateCloneState(pState)
{
	const tmpErrors = [];
	const tmpID = parseInt(pState && pState.IDCustomer, 10);
	if (!(tmpID > 0))
	{
		tmpErrors.push('A customer is required.');
	}
	if (!pState || !pState.Name || !NAME_REGEX.test(pState.Name))
	{
		tmpErrors.push('Name must start with a letter and contain only letters, numbers, and underscores.');
	}
	if (!pState || !pState.LakeSource)
	{
		tmpErrors.push('A source lake table is required.');
	}
	else if (pState.LakeSource === `lake/C${tmpID}_${pState.Name}`)
	{
		tmpErrors.push('The new projection name must differ from the source table.');
	}
	if (!pState || !pState.GUIDTemplate || pState.GUIDTemplate.indexOf('{~D:Record.') < 0)
	{
		tmpErrors.push('A row key (GUID template) referencing a source column is required.');
	}
	return { Valid: tmpErrors.length === 0, Errors: tmpErrors };
}

module.exports =
{
	NAME_REGEX,
	OPERATION_TYPES,
	jsonTypeToMeadow,
	dbTypeToJsonType,
	buildSourceAddress,
	buildExtractionBody,
	validateExtractionState,
	buildPassthroughCloneBody,
	validateCloneState,
};
